import { apiConfig } from './config';
import { ApiException, type ApiError } from './types';
import { getAuthToken } from '@/auth/token';

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  /**
   * If set, sent as the {@code Idempotency-Key} HTTP header. The backend
   * caches the 2xx response for 24h keyed by path + this value. Use for any
   * customer-driven create that the user might retry (warranty register,
   * claim submit, attachment begin, etc.).
   */
  idempotencyKey?: string;
  token?: string;
  signal?: AbortSignal;
}

const REPLAY_HEADER = 'idempotency-replay';

export interface ApiResponse<T> {
  data: T;
  /** True when the backend served this response from its idempotency cache. */
  replayed: boolean;
  status: number;
}

/**
 * Typed fetch wrapper. Throws {@link ApiException} on non-2xx, returns the
 * parsed JSON body on success, returns {@code undefined as T} for 204.
 *
 * Most callers use {@link apiRequest}; reach for {@link apiRequestRaw} if you
 * need to inspect headers (e.g. {@code Idempotency-Replay}).
 */
export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  return (await apiRequestRaw<T>(path, options)).data;
}

export async function apiRequestRaw<T>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<ApiResponse<T>> {
  const url = path.startsWith('http') ? path : `${apiConfig.baseUrl}${path}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...options.headers
  };
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (options.idempotencyKey) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }
  // Bearer token: explicit override wins; otherwise auto-attach the current
  // OIDC session token from the module-level slot maintained by AuthContext.
  const token = options.token ?? getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal
  });

  if (!response.ok) {
    let error: ApiError;
    try {
      error = (await response.json()) as ApiError;
    } catch {
      error = {
        code: 'UNKNOWN',
        message: response.statusText || `HTTP ${response.status}`,
        status: response.status
      };
    }
    throw new ApiException(error);
  }

  const replayed = response.headers.get(REPLAY_HEADER) === 'true';

  // 204 No Content
  if (response.status === 204) {
    return { data: undefined as unknown as T, replayed, status: response.status };
  }
  const data = (await response.json()) as T;
  return { data, replayed, status: response.status };
}

/**
 * Generate a fresh idempotency key. UUID v4 from the browser's crypto API.
 * Callers should hold onto this for the duration of the user's "save" intent
 * (i.e. don't regenerate per render).
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
